/**
 * What the portal and a mockup's frame say to each other (session 49,
 * `.docs/49-mockup-pins-and-guests.md` §Pins; version 2 is session 55,
 * `.docs/55-mockup-comments.md`).
 *
 * Two halves, one vocabulary. The parent — `usePinBridge` — sends the pins it wants drawn,
 * which one is open, and whether the page is being commented on; the frame — `bridge.ts`, built
 * into `/mockup-pins.js` and appended to every page the preview serves — sends where somebody
 * asked to comment (a click, a drag, a selection, a right-click), which pin was pressed, where
 * the open one is on the screen, and which pins it could draw. **No comment's words ever
 * cross**: the frame runs the mockup's own scripts, which may load anything from anywhere, so
 * the frame is told numbers and places — and, for a pin on text, its quote: the page's own words,
 * which the bridge took from a selection and needs to find them again. Nobody's comment, name or
 * reply.
 *
 * The parent parses everything it hears with these schemas, because the mockup's scripts can
 * post to it too. The frame imports the types only (`import type`), so its build carries no
 * import — `scripts/mockup-pins.ts` refuses one.
 */

import { MOCKUP_PIN_SELECTOR_MAX, MOCKUP_QUOTE_MAX, MOCKUP_VIEWPORT_MAX } from '@fareast/shared'
import { z } from 'zod'

/** The tag on every message, so neither side mistakes somebody else's `postMessage` for one. */
export const PIN_TAG = 'fes-pins'

/**
 * The protocol's version. A bridge cached from before a change ignores what it cannot read, and
 * the parent ignores it. 2 is session 55's: areas, text, the open pin's place, and modes.
 */
export const PIN_VERSION = 2

const Fraction = z.number().min(0).max(1)
const Extent = z.number().gt(0).max(1)

/**
 * Where on a page — everything a pin is but its page and the page's width, which the parent
 * knows or is told beside it. A point; with `w`/`h` an area whose top-left is `x`/`y`; with
 * `quote`/`quote_start` some text. The pairs are the server's to insist on
 * (`MockupPinSchema`); the bridge only ever makes whole ones.
 */
export const PinSpotSchema = z.strictObject({
  selector: z.string().min(1).max(MOCKUP_PIN_SELECTOR_MAX),
  x: Fraction,
  y: Fraction,
  doc_x: Fraction,
  doc_y: Fraction,
  w: Extent.optional(),
  h: Extent.optional(),
  quote: z.string().min(1).max(MOCKUP_QUOTE_MAX).optional(),
  quote_start: z.int().min(0).max(10_000_000).optional(),
})

export type PinSpot = z.infer<typeof PinSpotSchema>

/** A pin to draw: its number, where it is, and whether its thread is resolved (shown faded). */
export type PinMark = PinSpot & { readonly n: number; readonly resolved: boolean }

/**
 * What a click in the page does. `browse`: the page's own — a link is followed — and a
 * selection or a right-click starts a comment. `comment`: a click pins a spot and a drag an area,
 * and the page never hears either.
 */
export type PinMode = 'browse' | 'comment'

/* ------------------------------------------------------------- parent → frame */

export type ParentMessage =
  | { readonly fes: typeof PIN_TAG; readonly v: typeof PIN_VERSION; readonly type: 'hello' }
  | {
      readonly fes: typeof PIN_TAG
      readonly v: typeof PIN_VERSION
      readonly type: 'state'
      /** The pins on the page the frame is showing — never another page's. */
      readonly pins: readonly PinMark[]
      readonly mode: PinMode
      /**
       * Whether the person looking may comment at all. Off, a selection is only a selection and
       * a right-click is the browser's.
       */
      readonly gestures: boolean
      /** The spot a comment being written is about, drawn apart from the numbered ones. */
      readonly draft: PinSpot | null
      /** The pin whose thread is open: lit, and the one whose place the frame reports. */
      readonly active: number | null
    }
  | {
      readonly fes: typeof PIN_TAG
      readonly v: typeof PIN_VERSION
      readonly type: 'focus'
      readonly n: number
    }

/* ------------------------------------------------------------- frame → parent */

/** A coordinate in the frame's own viewport, in CSS pixels — bounded, because anybody can post. */
const Coordinate = z.number().min(-1_000_000).max(1_000_000)

export const FrameMessageSchema = z.discriminatedUnion('type', [
  /** The bridge is listening, on this page — sent when it starts and in answer to `hello`. */
  z.strictObject({
    fes: z.literal(PIN_TAG),
    v: z.literal(PIN_VERSION),
    type: z.literal('ready'),
    path: z.string().max(2_000),
  }),
  /**
   * Somebody asked to comment here — by a click or a drag while commenting, by the button under a
   * selection, by Ctrl/⌘+Alt+M, or by a right-click.
   */
  z.strictObject({
    fes: z.literal(PIN_TAG),
    v: z.literal(PIN_VERSION),
    type: z.literal('place'),
    path: z.string().max(2_000),
    spot: PinSpotSchema,
    /** How wide the page is, as the pin will say it was left at. */
    viewport_w: z.int().min(1).max(MOCKUP_VIEWPORT_MAX),
  }),
  /** Somebody pressed a numbered pin. */
  z.strictObject({
    fes: z.literal(PIN_TAG),
    v: z.literal(PIN_VERSION),
    type: z.literal('select'),
    n: z.int().min(1),
  }),
  /**
   * A key pressed inside the frame, where the keyboard is once somebody has clicked the page:
   * Escape, or `C` for comment mode — never while they are typing into the mockup's own form.
   */
  z.strictObject({
    fes: z.literal(PIN_TAG),
    v: z.literal(PIN_VERSION),
    type: z.literal('key'),
    key: z.enum(['escape', 'comment']),
  }),
  /**
   * Where the pin of the comment being written — or else of the open thread — is now, in the
   * frame's viewport: the marker's tip, which the portal puts the thread beside. Null when it is
   * not on the page at this width.
   */
  z.strictObject({
    fes: z.literal(PIN_TAG),
    v: z.literal(PIN_VERSION),
    type: z.literal('where'),
    at: z.strictObject({ x: Coordinate, y: Coordinate }).nullable(),
  }),
  /** The numbered pins the frame could draw on this page, at this width. */
  z.strictObject({
    fes: z.literal(PIN_TAG),
    v: z.literal(PIN_VERSION),
    type: z.literal('shown'),
    ns: z.array(z.int().min(1)).max(10_000),
  }),
])

export type FrameMessage = z.infer<typeof FrameMessageSchema>

/**
 * The page a frame's address is, inside a version — `index.html`, `about/index.html` — or null
 * for an address outside the version (a link the mockup followed somewhere else).
 *
 * The version's base is `/preview/<public_id>/v<n>/`; a folder is its `index.html`, as the
 * preview serves it; segments are decoded and composed (NFC) as the files' paths were stored.
 */
export function pageOf(path: string, versionUrl: string): string | null {
  const root = versionRootOf(versionUrl)
  if (root === null || !path.startsWith(root)) return null
  const rest = path.slice(root.length)
  try {
    const decoded = rest.split('/').map(decodeURIComponent).join('/').normalize('NFC')
    return decoded === '' || decoded.endsWith('/') ? `${decoded}index.html` : decoded
  } catch {
    return null
  }
}

/**
 * `/preview/<public_id>/v<n>/` out of any address inside a version — its entry page's included,
 * which may sit in a folder (`v2/pages/home.html`). The version is the `v<n>` segment straight
 * after the link's 22 characters, wherever the previews are served from.
 */
export function versionRootOf(url: string): string | null {
  const segments = new URL(url).pathname.split('/')
  const at = segments.findIndex(
    (segment, index) =>
      index > 0 && /^v[1-9]\d{0,5}$/.test(segment) && LINK_ID.test(segments[index - 1] ?? ''),
  )
  return at === -1 ? null : `${segments.slice(0, at + 1).join('/')}/`
}

const LINK_ID = /^[A-Za-z0-9_-]{22}$/

/** A page's address inside a version — what the frame is pointed at to show another page. */
export function pageUrlOf(versionUrl: string, page: string): string | null {
  const root = versionRootOf(versionUrl)
  if (root === null) return null
  const encoded = page === 'index.html' ? '' : page.split('/').map(encodeURIComponent).join('/')
  return `${new URL(versionUrl).origin}${root}${encoded}`
}
